/* wk-dob-capture.js — v2.3 (Nov 2026)
 * Age gate for the cart drawer and the cart page.
 * Pairs with snippets/dob-capture.liquid v6.0 and snippets/cart-summary.liquid v17.1.
 *
 * - Accepts a birthdate typed any sensible way (02/24/1982, 2-24-82, 02241982, 02/241982, 1982-02-24)
 *   and stores it on the cart as YYYY-MM-DD in `customer_dob`, with `age_attestation` = "true"
 *   (same keys and format as v1, so order records are unchanged).
 * - Inserts separators as soon as each part is complete (phone keypads have none):
 *   02 -> 02/ -> 02/24/ -> 02/24/1982; a year-first entry formats as 1982- -> 1982-02- -> 1982-02-24.
 *   Backspace removes the separator first, then digits. Separators the shopper types are left alone.
 * - Keeps Checkout and the express-checkout buttons locked until the date parses, the shopper
 *   is 21+, and the box is ticked.
 * - Shows a plain message when something is missing; a tap on a locked Checkout button surfaces
 *   it too (via the shield in cart-summary.liquid) instead of doing nothing.
 * - Re-wires itself whenever Shopify re-renders the cart (quantity changes, removals).
 */
(() => {
  'use strict';

  const ROOT_SEL = '[data-wk-dobcap]';
  const DOB_SEL = '[data-wk-dobcap-dob]';
  const ATTEST_SEL = '[data-wk-dobcap-attest]';
  const ERROR_SEL = '[data-wk-dobcap-error]';
  const STATUS_SEL = '[data-wk-dobcap-status]';
  const SUMMARY_SEL = '[data-wk-cart-summary]';
  const SHIELD_SEL = '[data-wk-checkout-shield]';
  const CHECKOUT_BTN_SEL = '#checkout';
  const ACCEL_WRAP_SEL = '#Wk-Accelerated-Buttons';
  const MIN_AGE = 21;
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const qs = (sel, ctx) => (ctx || document).querySelector(sel);
  const qsa = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  let lastPersistedDob = null;
  let persistInFlight = false;
  let persistPending = null;

  function setText(root, sel, msg) {
    const el = qs(sel, root);
    if (!el) return;
    el.textContent = msg || '';
    el.hidden = !msg;
  }
  const setError = (root, msg) => setText(root, ERROR_SEL, msg);
  const setStatus = (root, msg) => setText(root, STATUS_SEL, msg);

  function lockUI(locked) {
    qsa(CHECKOUT_BTN_SEL).forEach((btn) => {
      btn.classList.toggle('wk-locked', locked);
      btn.disabled = !!locked;
      if (locked) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
    });
    qsa(ACCEL_WRAP_SEL).forEach((wrap) => {
      wrap.classList.toggle('wk-locked', locked);
      if (locked) wrap.setAttribute('aria-hidden', 'true');
      else wrap.removeAttribute('aria-hidden');
    });
  }

  /* ---------- date parsing ---------- */

  function daysInMonth(y, m) {
    return new Date(y, m, 0).getDate(); // m is 1-12
  }

  // "82" -> 1982, "05" -> 2005 (two-digit years above the current year's last two digits are last century)
  function expandYear(yy) {
    const now = new Date().getFullYear();
    const century = Math.floor(now / 100) * 100;
    let y = century + yy;
    if (yy > now % 100) y -= 100;
    return y;
  }

  // An unseparated run of digits: MMDDYYYY, YYYYMMDD or MMDDYY.
  function fromDigits(digits) {
    if (digits.length === 8) {
      if (+digits.slice(0, 2) > 12) { // can't be a month, so it's year-first
        return { y: +digits.slice(0, 4), m: +digits.slice(4, 6), d: +digits.slice(6, 8) };
      }
      return { m: +digits.slice(0, 2), d: +digits.slice(2, 4), y: +digits.slice(4, 8) };
    }
    if (digits.length === 6) {
      return { m: +digits.slice(0, 2), d: +digits.slice(2, 4), y: expandYear(+digits.slice(4, 6)) };
    }
    return null;
  }

  // Returns { y, m, d, iso } or null.
  // Accepts M/D/YYYY, MM-DD-YY, YYYY-MM-DD, MMDDYYYY, YYYYMMDD, MMDDYY, and single-separator forms like 02/241982.
  function parseDOB(str) {
    const s = String(str || '').trim();
    if (!s) return null;
    const parts = s.split(/[^0-9]+/).filter(Boolean);
    let r = null;

    if (parts.length === 3) {
      const [a, b, c] = parts;
      if (a.length === 4) {
        r = { y: +a, m: +b, d: +c };
      } else if (c.length === 4) {
        r = { m: +a, d: +b, y: +c };
      } else if (c.length === 2) {
        r = { m: +a, d: +b, y: expandYear(+c) };
      }
    } else if (parts.length === 2) {
      const [a, b] = parts;
      if (a.length <= 2 && b.length === 6) {            // 02/241982
        r = { m: +a, d: +b.slice(0, 2), y: +b.slice(2) };
      } else if (a.length <= 2 && b.length === 5) {     // 2/41982
        r = { m: +a, d: +b.slice(0, 1), y: +b.slice(1) };
      } else if (a.length <= 2 && b.length === 4) {     // 02/2482
        r = { m: +a, d: +b.slice(0, 2), y: expandYear(+b.slice(2)) };
      } else if (a.length === 4 && b.length === 4) {    // 1982/0224 or 0224/1982
        if (+a.slice(0, 2) > 12) r = { y: +a, m: +b.slice(0, 2), d: +b.slice(2) };
        else r = { m: +a.slice(0, 2), d: +a.slice(2), y: +b };
      } else if (a.length === 4 && b.length === 2 && +a.slice(0, 2) <= 12) { // 0224/82
        r = { m: +a.slice(0, 2), d: +a.slice(2), y: expandYear(+b) };
      }
    }

    if (!r) r = fromDigits(parts.join(''));
    if (!r) return null;

    const { y, m, d } = r;
    if (!(y >= 1900 && m >= 1 && m <= 12 && d >= 1 && d <= daysInMonth(y, m))) return null;
    const pad = (n) => String(n).padStart(2, '0');
    return { y, m, d, iso: `${y}-${pad(m)}-${pad(d)}` };
  }

  function ageOn(dob, now) {
    let age = now.getFullYear() - dob.y;
    const mm = now.getMonth() + 1;
    if (mm < dob.m || (mm === dob.m && now.getDate() < dob.d)) age--;
    return age;
  }

  function prettyDate(dob) {
    return `${MONTHS[dob.m - 1]} ${dob.d}, ${dob.y}`;
  }

  /* ---------- type-ahead formatting ---------- */

  // Canonical display for a run of digits: 02/24/1982, or 1982-02-24 when it starts with a year.
  // With `eager`, a separator is added as soon as a part is complete (02 -> "02/").
  function canonical(digits, eager) {
    const yearFirst = digits.length >= 2 && +digits.slice(0, 2) > 12;
    const groups = yearFirst ? [4, 2, 2] : [2, 2, 4];
    const sep = yearFirst ? '-' : '/';
    let out = '';
    let pos = 0;
    for (let g = 0; g < groups.length && pos < digits.length; g++) {
      const part = digits.slice(pos, pos + groups[g]);
      out += part;
      pos += part.length;
      const complete = part.length === groups[g];
      const last = g === groups.length - 1;
      if (complete && !last && (eager || pos < digits.length)) out += sep;
    }
    return out;
  }

  // Insert separators as the shopper types digits. Leaves the value alone if they typed their own
  // separators in other places (e.g. 2/24/1982) or anything that isn't a digit, slash or dash.
  function autoFormat(input, deleting) {
    const value = input.value || '';
    if (!/^[0-9\/\-]*$/.test(value)) { input.dataset.wkPrev = value; return; }
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (!digits) { input.dataset.wkPrev = value; return; }
    const target = canonical(digits, !deleting);

    // Every separator the shopper typed must sit where the canonical form has one.
    for (let i = 0; i < value.length; i++) {
      const ch = value[i];
      if (ch === '/' || ch === '-') {
        if (i >= target.length || (target[i] !== '/' && target[i] !== '-')) { input.dataset.wkPrev = value; return; }
      }
    }

    if (value !== target) {
      input.value = target;
      try { input.setSelectionRange(target.length, target.length); } catch (_) { /* not supported on this input */ }
    }
    input.dataset.wkPrev = target;
  }

  /* ---------- validation + persistence ---------- */

  function validate(root, showError) {
    const dobInput = qs(DOB_SEL, root);
    const attestInput = qs(ATTEST_SEL, root);
    if (!dobInput || !attestInput) return { ok: false, iso: '' };

    const raw = (dobInput.value || '').trim();
    const digitCount = raw.replace(/\D/g, '').length;
    const attested = !!attestInput.checked;
    const dob = parseDOB(raw);

    let ok = true;
    let msg = '';
    let status = '';

    if (!dob) {
      ok = false;
      msg = raw ? 'Enter your date of birth as MM/DD/YYYY.' : 'Enter your date of birth (MM/DD/YYYY).';
    } else {
      const age = ageOn(dob, new Date());
      if (age < 0) {
        ok = false;
        msg = 'That date is in the future.';
      } else if (age < MIN_AGE) {
        ok = false;
        msg = 'You must be 21 or older to buy wine.';
      } else {
        status = prettyDate(dob);
      }
    }

    if (!attested) {
      ok = false;
      if (!msg) msg = 'Tick the box to confirm you are 21+ and accept the Terms.';
    }

    setStatus(root, status ? (ok ? '\u2713 Verified 21+ \u2014 ' + status : 'Reads as ' + status) : '');

    const surface = showError || (!dob && digitCount >= 8);
    if (!ok && surface) setError(root, msg);
    if (ok) setError(root, '');

    lockUI(!ok);
    return { ok, iso: dob ? dob.iso : '' };
  }

  async function persist(iso) {
    if (!iso || iso === lastPersistedDob) return;
    if (persistInFlight) { persistPending = iso; return; }

    persistInFlight = true;
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: { customer_dob: iso, age_attestation: 'true' } })
      });
      if (res.ok) {
        lastPersistedDob = iso;
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (e) {
      // Non-fatal: the gate is still enforced client-side and re-checked on the next render.
    } finally {
      persistInFlight = false;
      const next = persistPending;
      persistPending = null;
      if (next && next !== lastPersistedDob) persist(next);
    }
  }

  /* ---------- wiring ---------- */

  function wire(root) {
    if (!root || root.dataset.wkInit === 'true') return;
    root.dataset.wkInit = 'true';

    const dobInput = qs(DOB_SEL, root);
    const attestInput = qs(ATTEST_SEL, root);
    if (!dobInput || !attestInput) return;

    // Already stored on the cart by an earlier render? Then don't re-send it.
    const stored = (root.dataset.wkDobcapStored || '').trim();
    if (stored && !lastPersistedDob) lastPersistedDob = stored;

    dobInput.dataset.wkPrev = dobInput.value || '';

    const run = (showError) => {
      const r = validate(root, showError);
      if (r.ok) persist(r.iso);
      return r;
    };

    dobInput.addEventListener('input', (e) => {
      const prev = dobInput.dataset.wkPrev || '';
      const deleting = (e && typeof e.inputType === 'string' && e.inputType.indexOf('delete') === 0) || (dobInput.value || '').length < prev.length;
      autoFormat(dobInput, deleting);
      run(false);
    });
    dobInput.addEventListener('change', () => run(true));
    dobInput.addEventListener('blur', () => { if ((dobInput.value || '').trim()) run(true); });
    attestInput.addEventListener('change', () => run(true));

    run(false);
  }

  function setupAll() {
    qsa(ROOT_SEL).forEach(wire);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setupAll);
  else setupAll();
  document.addEventListener('cart:refresh', setupAll);
  document.addEventListener('cart:update', setupAll);

  const mo = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.addedNodes && m.addedNodes.length) { setupAll(); break; }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  function rootFor(el) {
    const summary = el.closest(SUMMARY_SEL);
    return (summary && qs(ROOT_SEL, summary)) || qs(ROOT_SEL);
  }

  // A tap on a locked Checkout (or its shield) explains what's missing instead of doing nothing.
  document.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;
    const shield = target.closest(SHIELD_SEL);
    const btn = shield ? null : target.closest('#checkout, [name="checkout"], .cart__checkout-button');
    if (!shield && !btn) return;

    const locked = !!shield || btn.classList.contains('wk-locked') || btn.disabled;
    if (!locked) return;

    e.preventDefault();
    e.stopPropagation();

    const root = rootFor(shield || btn);
    if (!root) return;
    validate(root, true);
    try { root.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) { /* older browsers */ }
    const dobInput = qs(DOB_SEL, root);
    if (dobInput && !parseDOB(dobInput.value)) dobInput.focus({ preventScroll: true });
  }, true);
})();